"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HandicapCalcs = exports.CompositeMatchStrokes = exports.CompositeStrokes = exports.AdjustedMatchStrokes = exports.AdjustedStrokes = exports.StraightMatchStrokes = exports.StraightStrokes = exports.getPrimaryHdcpUsage = void 0;
exports.perHoleStrokeAllocations = perHoleStrokeAllocations;
exports.perHoleStrokes = perHoleStrokes;
/** Primary hdcp usage is the first array element; supports legacy object shape. */
const getPrimaryHdcpUsage = (r) => Array.isArray(r.hdcpUsage) ? r.hdcpUsage[0] : r.hdcpUsage;
exports.getPrimaryHdcpUsage = getPrimaryHdcpUsage;
/**
 * Allocate strokes across the full set of holes in play. When several holes share the same
 * stroke index (hdcp), strokes rotate through those holes in ascending hole-number order
 * before any of them receives a second stroke — e.g. two “stroke 1” holes get strokes 1 and 2,
 * then stroke-index 2 holes get 3 and 4, matching repeated stroke-index handling (nine-hole
 * card played twice as 1–9 / 1–9).
 *
 * For positive course handicap: strokes are applied in repeating order of (hdcp asc, holeNumber asc).
 * For plus handicap (negative playerStrokes): the first |playerStrokes| holes in that order get -1 each.
 */
function perHoleStrokeAllocations(playerStrokes, holes) {
    const n = holes.length;
    const counts = new Array(n).fill(0);
    if (n === 0)
        return counts;
    const order = holes
        .map((h, idx) => ({
        idx,
        hdcp: h.hdcp ?? 0,
        holeNumber: h.holeNumber,
    }))
        .sort((a, b) => a.hdcp - b.hdcp || a.holeNumber - b.holeNumber)
        .map((x) => x.idx);
    if (playerStrokes < 0) {
        const k = Math.abs(playerStrokes);
        for (let s = 0; s < k; s++) {
            counts[order[s % order.length]] -= 1;
        }
        return counts;
    }
    for (let s = 0; s < playerStrokes; s++) {
        counts[order[s % order.length]] += 1;
    }
    return counts;
}
/**
 * Single-hole helper assuming unique stroke ranks 1–18 on the card (legacy).
 * Prefer {@link perHoleStrokeAllocations} when building a full round — it handles duplicate stroke indices.
 */
function perHoleStrokes(playerStrokes, holeHdcp) {
    if (playerStrokes < 0) {
        return holeHdcp <= Math.abs(playerStrokes) ? -1 : 0;
    }
    const secondStrokes = playerStrokes > 18 ? playerStrokes - 18 : 0;
    if (playerStrokes >= holeHdcp) {
        return secondStrokes >= holeHdcp ? 2 : 1;
    }
    return 0;
}
/** Shared per-player stroke calculation used by all Straight/Adjusted methods. */
const resolvePlayerStrokes = (registrations, scoreCode, courseData, totalHoles, reductionEffect) => registrations
    .filter((reg) => reg.scoreCode === scoreCode)
    .reduce((acc, reg) => {
    const teeSet = courseData.teeSets.find((ts) => ts.teeName === reg.teeSet) ?? courseData.teeSets[0];
    const gender = reg.gender || 'mens';
    const slope = teeSet[`${gender}Slope`];
    const rating = teeSet[`${gender}Rating`];
    const par = teeSet.holes.reduce((s, h) => s + h.par, 0);
    return (acc +
        (Math.round(((reg.playerHandicap ?? 0) *
            (totalHoles / 18) *
            ((slope ?? 113) / 113) +
            (rating ?? 0) -
            par) *
            reductionEffect) || 0));
}, 0);
const StraightStrokes = (round, viewEventData, registrations, scoreCode, markerScoreCode) => {
    const courseData = viewEventData.courseData.find((c) => c._id === round.courseID);
    return {
        playerStrokes: resolvePlayerStrokes(registrations, scoreCode, courseData, round.totalHoles, 1),
        markerStrokes: resolvePlayerStrokes(registrations, markerScoreCode, courseData, round.totalHoles, 1),
    };
};
exports.StraightStrokes = StraightStrokes;
const StraightMatchStrokes = (round, viewEventData, registrations, scoreCode, markerScoreCode) => {
    const courseData = viewEventData.courseData.find((c) => c._id === round.courseID);
    const p = resolvePlayerStrokes(registrations, scoreCode, courseData, round.totalHoles, 1);
    const m = resolvePlayerStrokes(registrations, markerScoreCode, courseData, round.totalHoles, 1);
    return { playerStrokes: Math.abs(p - m), markerStrokes: Math.abs(m - p) };
};
exports.StraightMatchStrokes = StraightMatchStrokes;
const AdjustedStrokes = (round, viewEventData, registrations, scoreCode, markerScoreCode) => {
    const courseData = viewEventData.courseData.find((c) => c._id === round.courseID);
    const hdcpUsage = (0, exports.getPrimaryHdcpUsage)(round);
    const re = hdcpUsage && typeof hdcpUsage.indReduction === 'number'
        ? 1 - hdcpUsage.indReduction / 100
        : 1;
    return {
        playerStrokes: resolvePlayerStrokes(registrations, scoreCode, courseData, round.totalHoles, re),
        markerStrokes: resolvePlayerStrokes(registrations, markerScoreCode, courseData, round.totalHoles, re),
    };
};
exports.AdjustedStrokes = AdjustedStrokes;
const AdjustedMatchStrokes = (round, viewEventData, registrations, scoreCode, markerScoreCode) => {
    const courseData = viewEventData.courseData.find((c) => c._id === round.courseID);
    const hdcpUsage = (0, exports.getPrimaryHdcpUsage)(round);
    const re = hdcpUsage && typeof hdcpUsage.indReduction === 'number'
        ? 1 - hdcpUsage.indReduction / 100
        : 1;
    const p = resolvePlayerStrokes(registrations, scoreCode, courseData, round.totalHoles, re);
    const m = resolvePlayerStrokes(registrations, markerScoreCode, courseData, round.totalHoles, re);
    return { playerStrokes: Math.abs(p - m), markerStrokes: Math.abs(m - p) };
};
exports.AdjustedMatchStrokes = AdjustedMatchStrokes;
const CompositeStrokes = (round, viewEventData, registrations, scoreCode, markerScoreCode) => {
    const courseData = viewEventData.courseData.find((c) => c._id === round.courseID);
    const hdcpUsagePrim = (0, exports.getPrimaryHdcpUsage)(round);
    const hdcpMethod = hdcpUsagePrim?.hdcpComposite?.type ?? '';
    const sortedPlayer = registrations
        .filter((r) => r.scoreCode === scoreCode)
        .sort((a, b) => (a.playerHandicap ?? 0) - (b.playerHandicap ?? 0));
    const sortedMarker = registrations
        .filter((r) => r.scoreCode === markerScoreCode)
        .sort((a, b) => (a.playerHandicap ?? 0) - (b.playerHandicap ?? 0));
    const calcOne = (reg, position) => {
        const teeSet = courseData.teeSets.find((ts) => ts.teeName === reg.teeSet) ?? courseData.teeSets[0];
        const gender = reg.gender || 'mens';
        const slope = teeSet[`${gender}Slope`];
        const rating = teeSet[`${gender}Rating`];
        const par = teeSet.holes.reduce((s, h) => s + h.par, 0);
        const weight = (hdcpUsagePrim?.hdcpComposite?.build?.[position]?.hdcpWeight ?? 0) / 100;
        const re = 1 - (hdcpUsagePrim?.indReduction ?? 0) / 100;
        return (((reg.playerHandicap ?? 0) * (round.totalHoles / 18) * ((slope ?? 113) / 113) +
            (rating ?? 0) -
            par) *
            weight *
            re);
    };
    if (hdcpMethod.toLowerCase() === 'low-high') {
        const ps = calcOne(sortedPlayer[0], 0) + calcOne(sortedPlayer[sortedPlayer.length - 1], 1);
        const ms = sortedMarker.length
            ? calcOne(sortedMarker[0], 0) + calcOne(sortedMarker[sortedMarker.length - 1], 1)
            : 0;
        return { playerStrokes: ps, markerStrokes: ms };
    }
    const ps = Math.round(sortedPlayer.reduce((acc, r, i) => acc + calcOne(r, i), 0));
    const ms = sortedMarker.length
        ? Math.round(sortedMarker.reduce((acc, r, i) => acc + calcOne(r, i), 0))
        : 0;
    return { playerStrokes: ps, markerStrokes: ms };
};
exports.CompositeStrokes = CompositeStrokes;
const CompositeMatchStrokes = (round, viewEventData, registrations, scoreCode, markerScoreCode) => {
    const { playerStrokes, markerStrokes } = (0, exports.CompositeStrokes)(round, viewEventData, registrations, scoreCode, markerScoreCode);
    return {
        playerStrokes: Math.abs(playerStrokes - markerStrokes),
        markerStrokes: Math.abs(markerStrokes - playerStrokes),
    };
};
exports.CompositeMatchStrokes = CompositeMatchStrokes;
const functionMap = {
    straightStrokes: exports.StraightStrokes,
    straightMatchStrokes: exports.StraightMatchStrokes,
    adjustedStrokes: exports.AdjustedStrokes,
    adjustedMatchStrokes: exports.AdjustedMatchStrokes,
    compositeStrokes: exports.CompositeStrokes,
    compositeMatchStrokes: exports.CompositeMatchStrokes,
};
const HandicapCalcs = (functionName, props) => {
    const fn = functionMap[functionName];
    if (!fn)
        throw new Error(`HandicapCalcs: unknown function "${functionName}"`);
    return fn(props.round, props.viewEventData, props.registrations, props.scoreCode, props.markerScoreCode);
};
exports.HandicapCalcs = HandicapCalcs;
